<template>
  <div class="space-editor">
    <m-content
      class="_header"
      :padding-x="11"
    >
      <m-tab
        v-for="tab in tabs"
        :key="tab.value"
        :title="tab.text"
        :active="selectedTab === tab.value"
        small
        @click="selectedTab = tab.value"
      />
      <m-divider none />
    </m-content>
    <scroll-container
      :class="['_body', '-update']"
      no-padding-bottom
    >
      <m-content :padding-x="11">
        <div
          v-if="selectedTab === 'general'"
          class="_general"
        >
          <space-general-form
            v-model:space="space"
            :read-only="!writeAccess"
            :with-archived="withArchived"
            @enter="submit"
          />
          <m-form-item class="_actions">
            <m-btn
              small
              super-light
              :href="$t('spaceList.helpCenterLink')"
              hide-border
              icon="question-circle"
              target="_blank"
            >
              {{ $t('spaceList.learnMore') }}
            </m-btn>
            <m-tooltip
              :disabled="formIsValid"
            >
              <m-btn
                v-if="writeAccess"
                color="primary"
                :loading="submitLoading"
                :disabled="loading || !formIsValid"
                @click="submit"
              >
                {{ $t('general.update') }}
              </m-btn>
              <template #title>
                {{ formValidationHint }}
              </template>
            </m-tooltip>
          </m-form-item>
          <m-form-item
            v-if="fullAccess"
            class="_danger-actions _form-item"
            :label="$t('spaceEditor.dangerZoneLabel')"
          >
            <m-btn-group direction="vertical">
              <m-btn
                class="_btn"
                icon="archive"
                :loading="archiveLoading"
                :disabled="loading"
                @click="toggleArchive"
              >
                <div class="_message">
                  <div class="_text">
                    {{ !isArchived ? $t('spaceEditor.archiveText') : $t('spaceEditor.restoreText') }}
                  </div>
                  <div class="_subtext">
                    {{ !isArchived ? $t('spaceEditor.archiveSubtext') : $t('spaceEditor.restoreSubtext') }}
                  </div>
                </div>
              </m-btn>
              <m-btn
                class="_btn"
                icon="delete"
                :icon-color="colors.red.base"
                :loading="deleteSpacesLoading"
                :disabled="loading"
                @click="showDeleteModal"
              >
                <div class="_message">
                  <div class="_text -delete">
                    {{ $t('spaceEditor.deleteText') }}
                  </div>
                  <div class="_subtext">
                    {{ $t('spaceEditor.deleteSubtext') }}
                  </div>
                </div>
              </m-btn>
            </m-btn-group>
          </m-form-item>
        </div>
        <m-content
          v-if="selectedTab === 'members'"
          class="_members"
          :padding-bottom="11"
        >
          <list-header class="_list-header">
            <template #left>
              <m-dropdown
                v-if="fullAccess"
                v-model:value="showSpaceMembershipSelector"
                :title="$t('spaceMembers.addMembers')"
              >
                <m-btn>
                  {{ $t('spaceMembers.addMembers') }}
                </m-btn>
                <template #overlay>
                  <space-membership-selector
                    :space="space"
                    @select="handleInviteMembers"
                  />
                </template>
              </m-dropdown>
            </template>
            <template #right>
              <list-string-filter
                small
                :disabled="loading.value"
                @input="val => memberSearch = val"
              />
            </template>
          </list-header>
          <m-table
            :columns="columns"
            :data-source="rows"
            class="_table"
            hide-vertical-border
            row-key="user.uid"
            :pagination="{ pageSize: 7 }"
          >
            <template #user="{ user }">
              <user-display
                :user="user"
                xl
              >
                <template #subName>
                  <div class="_email">
                    {{ emailOf(user) }}
                  </div>
                </template>
              </user-display>
            </template>
            <template #role="{ row }">
              <m-select
                :items="roles"
                :value="role(row)"
                hide-border
                :read-only="!fullAccess"
                item-text="value.text"
                small
                controlled
                @item-click="clickRole(row, $event)"
                @change="changeRole(row, $event)"
              >
                <template #item="item">
                  <div :style="item.item.type === 'remove' ? { color: colors.red.base } : {}">
                    {{ item.item.text }}
                  </div>
                </template>
              </m-select>
            </template>
          </m-table>
        </m-content>
        <space-permissions
          v-if="selectedTab === 'permissions'"
          v-model:space="space"
          :read-only="!fullAccess"
          :loading="updateSpaceLoading"
          @update:permissions="updatePermissions"
        />
      </m-content>
    </scroll-container>
  </div>
</template>

<script setup>
import ListHeader from '@/components/list/ListHeader.vue';
import ListStringFilter from '@/components/list/ListStringFilter.vue';
import ScrollContainer from '@/components/page/ScrollContainer.vue';
import SpaceGeneralForm from '@/components/spaces/SpaceGeneralForm.vue';
import SpaceMembershipSelector from '@/components/spaces/SpaceMembershipSelector.vue';
import SpacePermissions from '@/components/spaces/SpacePermissions.vue';
import UserDisplay from 'shared/components/UserDisplay.vue';
import colors from 'shared/colors';
import useConfirmDialog from '@/composables/confirm-dialog';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useSnackBar from '@/composables/snackbar';
import useSpace from '@/composables/space/space';
import useSpaceFormValidator from '@/composables/space/form-validator';
import useSpaces from '@/composables/space/spaces';
import { DateTime } from 'luxon';
import { accessPolicyType, failureCode } from 'shared/constants.json';
import { computed, ref, toRef, watch } from 'vue';
import { displayName, email as emailOf } from 'shared/composables/user';
import { failureCodeOf, logCatch } from '@/lib/logger/logger';
import { sortByAttr } from 'shared/lib/sort';
import { textByLang } from 'shared/lib/language';
import { useI18n } from 'vue-i18n';
import { userMatchesSearch } from 'shared/lib/user-search';

const props = defineProps({
  space: {
    type: Object,
    default: () => {},
  },
  withArchived: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const snackbar = useSnackBar();
const emit = defineEmits(['created', 'updated', 'archived', 'deleted', 'restored', 'close']);

const space = ref({ ...props.space });

const { formIsValid, formValidationHint } = useSpaceFormValidator(space);

const spaceProps = toRef(props, 'space');
watch(spaceProps, (s) => {
  if (s === undefined) {
    return;
  }
  space.value.permissions = s.permissions;
  space.value.accessRight = s.accessRight;
  space.value.properties = s.properties;
}, { deep: true });

const { loggedInUser, userLang } = useLoggedInUser();
const spaceSvc = useSpace(space);
const spaceMembers = computed(() => spaceSvc.members.value);

const spacesSvc = useSpaces();

const updatePermissions = (permissions) => {
  spacesSvc.updateSpace({ uid: space.value.uid, permissions }, { optimistic: false }).catch(logCatch(() => {
    snackbar.error();
  }));
};

const memberSearch = ref('');
const rows = computed(() => spaceMembers.value
  .filter((member) => userMatchesSearch(member.user, memberSearch.value)));

const columns = [
  {
    key: 'user',
    dataIndex: 'user',
    title: t('spaceMembers.header.name'),
    sorter: sortByAttr('user', (a, b) => displayName(a).localeCompare(displayName(b))),
    scopedSlots: { customRender: 'user' },
  },
  {
    key: 'role',
    dataIndex: 'role',
    title: t('spaceMembers.header.role'),
    sorter: sortByAttr('role', sortByAttr('label', (a, b) => textByLang(a, userLang).localeCompare(b, userLang))),
    scopedSlots: { customRender: 'role' },
  },
];

const showSpaceMembershipSelector = ref(false);
const handleInviteMembers = (invitees) => {
  if (invitees.length > 0) {
    spaceSvc.addMembers(invitees).then(() => {
      snackbar.success(t('success.added'));
    }).catch(logCatch(() => {
      snackbar.error(t('error.default'));
    }));
  }
  showSpaceMembershipSelector.value = false;
};
const propertyRole = (p) => ({ text: textByLang(p.label, userLang), type: 'property', property: { uid: p.uid } });
const memberRole = { text: t('spaceMembers.memberRole'), type: 'member' };
const removeRole = { text: t('general.remove'), type: 'remove' };
const roles = [
  ...spaceSvc.roles.value.map((p) => ({ value: propertyRole(p) })),
  { value: memberRole },
  { value: removeRole, selectable: false },
];
const role = (member) => {
  if (member.role === null) {
    return memberRole;
  }
  return propertyRole(member.role);
};

const confirmDialog = useConfirmDialog();
const clickRole = (member, value) => {
  if (value.type === 'remove') {
    changeRole(member, value);
  }
};
const changeRole = (member, value) => {
  const replaceRole = () => spaceSvc.replaceRole(member, value.property).catch(logCatch(() => {
    snackbar.error(t('error.default'));
  }));
  const removeMember = () => spaceSvc.removeMember(member).catch(logCatch((e) => {
    snackbar.error(failureCodeOf(e) === failureCode.lastAdmin ? t('error.lastAdmin') : t('error.default'));
  }, [failureCode.lastAdmin]));

  if (value.type === 'remove') {
    confirmDialog.confirm({
      title: t('spaceMembers.removeMemberPrompt'),
      okText: t('general.yesRemove'),
      cancelText: t('general.cancel'),
      onOk: removeMember,
    });
    return;
  }

  if (member.user.uid === loggedInUser.value.uid && member.role !== null) {
    confirmDialog.confirm({
      title: t('spaceMembers.removeRolePrompt'),
      okText: t('general.yesRemove'),
      cancelText: t('general.cancel'),
      onOk: replaceRole,
    });
    return;
  }
  replaceRole();
};

const tabs = computed(() => [
  {
    text: t('spaceEditor.generalTab'),
    value: 'general',
  },
  {
    text: t('spaceEditor.membersTab', { count: spaceMembers.value.length }),
    value: 'members',
  },
  {
    text: t('spaceEditor.permissionsTab'),
    value: 'permissions',
  },
]);
const selectedTab = ref(tabs.value[0].value);

const writeAccess = computed(() => [accessPolicyType.write, accessPolicyType.full].includes(space.value.accessRight));
const fullAccess = computed(() => [accessPolicyType.full].includes(space.value.accessRight));

const { createSpaceLoading, updateSpaceLoading, deleteSpacesLoading } = spacesSvc;
const submitLoading = computed(() => (createSpaceLoading.value || updateSpaceLoading.value) && !archiveLoading.value);
const loading = computed(() => createSpaceLoading.value || updateSpaceLoading.value || deleteSpacesLoading.value || archiveLoading.value);

const submit = () => {
  update(space.value);
};
const update = (space) => {
  const { uid, icon, image, color, title, parents, description } = space;
  spacesSvc.updateSpace({ uid, icon, image, color, title, parents, description }, { ignoreResponse: false }).then((data) => {
    emit('updated', data);
  }).catch(logCatch((err) => {
    if (failureCodeOf(err) === failureCode.circularReferenceNotAllowed) {
      snackbar.error(t('spaceEditor.validation.circularReference'));
      return;
    }
    snackbar.error();
  }, [failureCode.circularReferenceNotAllowed]));
};

const isArchived = computed(() => space.value.archivedAt !== null);
const archiveLoading = ref(false);
const toggleArchive = () => {
  archiveLoading.value = true;
  let fn = handleArchive;
  if (isArchived.value) {
    fn = handleRestore;
  }
  return fn().finally(() => {
    archiveLoading.value = false;
  });
};
const handleArchive = () => spacesSvc.updateSpace({ uid: space.value.uid, archivedAt: DateTime.local().toISO() }).then((res) => {
  space.value.archivedAt = res.archivedAt;
  snackbar.success(t('success.archived'));
  emit('archived');
});
const handleRestore = () => spacesSvc.updateSpace({ uid: space.value.uid, archivedAt: null }).then((res) => {
  space.value.archivedAt = res.archivedAt;
  snackbar.success(t('success.restored'));
  emit('restored');
});

const showDeleteModal = () => {
  const handleDelete = () => spacesSvc.deleteSpace(space.value).then(() => {
    snackbar.success(t('success.deleted'));
    emit('deleted');
  }).catch(logCatch((e) => {
    snackbar.error(failureCodeOf(e) === failureCode.lastAdmin ? t('error.lastAdmin') : t('error.default'));
  }, [failureCode.lastAdmin]));

  confirmDialog.confirm({
    title: t('spaceEditor.deleteModal.title', { optionTitle: space.value.title }),
    okText: t('spaceEditor.deleteModal.okText'),
    message: { text: t('spaceEditor.deleteModal.message') },
    secure: {
      question: t('spaceEditor.deleteModal.securityQuestion'),
      answer: space.value.title,
    },
    onOk() {
      handleDelete();
    },
  });
};
</script>

<style scoped lang="scss" type="text/scss">
  .space-editor {
    ._header {
      margin-bottom: 2rem;
    }

    ._body {
      &.-update {
        height: 52rem;
      }
    }

    ._general {
      ._actions {
        display: flex;
        justify-content: space-between;
      }

      ._danger-actions{
        &._form-item {
          ._label{
            color: $font-color-primary;
            font-weight: $font-weight-medium;
          }
        }

        ._btn {
          height: 100%;
        }

        ._message {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          white-space: normal;
          text-align: start;
          padding: 1rem;
          width: 100%;
          color: $font-color-secondary;

          ._text {
            color: $font-color-primary;

            &.-delete {
              color: $error-color;
            }
          }

          ._subtext {
            font-size: $font-size-2;
          }
        }
      }
    }

    ._members {
      :deep(._list-header) {
        margin-bottom: 1rem;
      }

      ._row {
        display: flex;
        justify-content: space-between;
        padding: .4rem 0;
      }

      ._email {
        max-width: 27rem;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

</style>
